/**
 * THE COMMERCIAL RECORD (`provider-outcome-1`).
 *
 * The convention this breaks is the **vendor report card**. A firm's page in
 * construction software usually ends in a verdict — a rating, a tier, a
 * traffic light. This one ends in a boundary:
 *
 *   awarded, authorized to proceed, and a procurement line opened at scope
 *   level — and past that, UNKNOWN.
 *
 * Everything above that line is real and complete, and rendered with
 * confidence. Everything below it says so.
 *
 * The two hardest sections to render honestly, and how they are handled:
 *
 *   EXECUTION HISTORY is structurally unknown — no execution table carries a
 *   provider reference and nine facts have no reachable writer. The contract
 *   is explicit that HIDING it is the worst option, because a reader who
 *   cannot see it assumes the commercial half is the whole picture. So it is
 *   rendered as a named list of missing instrumentation, never as an empty
 *   chart, a 0% bar, or a "no data" placeholder.
 *
 *   THE RECORD LIST is a list of dated rows with the arrows undrawn. No
 *   connector, no numbering, no "decision → outcome → next decision"
 *   layout — past the award every arrow is absent from the data, and a
 *   layout that implies one tells a lie the payload never states.
 *
 * It reads the SAME payload the scope-ownership panel already fetched. One
 * read, two panels: a second request would put two moments of one ledger
 * side by side.
 */
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription } from "../../components/ui/alert";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../../components/ui/collapsible";
import { shortId, formatDay } from "./exchangePresentation";
import {
  COMMERCIAL_COPY,
  presentCommercialLedger,
  canHeadline,
} from "./commercialLedgerPresentation";
import {
  Receipt,
  ChevronDown,
  Fingerprint,
  Split,
  PackageOpen,
  CalendarClock,
  MessageSquare,
  Ban,
  ListChecks,
  Clock,
  EyeOff,
} from "lucide-react";

/**
 * Attribution tone. There is no green and no red: this scale measures how
 * defensibly a fact attaches to a firm, not whether the firm did well.
 */
const ATTRIBUTION_TONE = {
  strong: "border-indigo-500/40 text-indigo-300 bg-indigo-500/10",
  moderate: "border-sky-500/40 text-sky-300 bg-sky-500/10",
  asserted: "border-violet-500/40 text-violet-300 bg-violet-500/10",
  weak: "border-border text-muted-foreground bg-muted/40",
  none: "border-border text-muted-foreground",
  unknown: "border-border text-muted-foreground bg-muted/40",
  conflict: "border-amber-500/40 text-amber-300 bg-amber-500/10",
};

const tone = (key) => ATTRIBUTION_TONE[key] || ATTRIBUTION_TONE.unknown;

const Caveat = ({ children, testId }) =>
  children ? (
    <p className="text-[10px] text-muted-foreground leading-relaxed" data-testid={testId}>
      {children}
    </p>
  ) : null;

const Ref = ({ reference }) => {
  if (!reference) return null;
  const id = reference.recordId || reference.id;
  if (!id) return null;
  return (
    <span className="font-mono text-[10px] text-muted-foreground">
      {reference.recordType || "record"} {shortId(id)}
    </span>
  );
};

/**
 * An attribution chip. `inferred` and `none` are rendered visually weaker
 * because the contract forbids them as a headline — the strength is the
 * point, not decoration.
 */
const AttributionChip = ({ attribution }) => (
  <Badge
    variant="outline"
    className={`text-[10px] ${tone(attribution.tone)} ${canHeadline(attribution.key) ? "" : "opacity-70"}`}
    title={attribution.meaning}
  >
    {attribution.label}
  </Badge>
);

const Section = ({ title, icon: Icon, lead, children, testId }) => (
  <section className="space-y-1.5" data-testid={testId}>
    <p className="text-[11px] font-medium flex items-center gap-1.5">
      {Icon ? <Icon className="w-3.5 h-3.5" /> : null}
      {title}
    </p>
    {lead ? <Caveat>{lead}</Caveat> : null}
    {children}
  </section>
);

const ReleaseRow = ({ release }) => (
  <div className="rounded border border-border/60 px-2.5 py-2 space-y-1" data-testid="ledger-release">
    <div className="flex flex-wrap items-start justify-between gap-2">
      <span className="text-xs font-medium">
        Package {shortId(release.packageId)}
        <span className="text-muted-foreground"> · {release.state || "state not recorded"}</span>
      </span>
      <Badge
        variant="outline"
        className={`text-[10px] ${tone(release.response.tone)}`}
        data-testid="release-response"
      >
        {release.response.label}
      </Badge>
    </div>
    <p className="text-[10px] text-muted-foreground">
      Prepared by {release.preparedBy || "not recorded"}
      {release.preparedAt ? ` on ${formatDay(release.preparedAt)}` : ""}
      {release.released && release.releasedBy ? ` · released by ${release.releasedBy}` : ""}
      {release.released && release.releasedAt && release.releasedAt !== "UNKNOWN"
        ? ` on ${formatDay(release.releasedAt)}`
        : ""}
    </p>
    <Caveat testId="response-basis">{release.response.basis}</Caveat>
    {release.response.evidence.length ? (
      <div className="flex flex-wrap gap-x-3">
        {release.response.evidence.map((reference, index) => (
          <Ref key={index} reference={reference} />
        ))}
      </div>
    ) : null}
  </div>
);

const ProcurementLine = ({ line }) => (
  <div className="rounded border border-border/60 px-2.5 py-2 space-y-1" data-testid="ledger-line">
    <div className="flex flex-wrap items-start justify-between gap-2">
      <span className="text-xs font-medium">{line.name || shortId(line.recordId)}</span>
      <AttributionChip attribution={line.attribution} />
    </div>
    <p className="text-[10px] text-muted-foreground">
      {line.status || "status not recorded"}
      {line.quantity !== null ? ` · ${line.quantity} ${line.unit || ""}` : ""}
      {line.expectedDelivery ? ` · planned ${formatDay(line.expectedDelivery)}` : ""}
      {line.actualDelivery ? ` · arrived ${formatDay(line.actualDelivery)}` : ""}
    </p>
    {line.corroboratedByCommercialRecord === false ? (
      <Caveat testId="line-uncorroborated">
        No live quote or proposal backs this line’s package for this firm, so it is reported as
        inferred rather than as scope this firm was awarded.
      </Caveat>
    ) : null}
    {line.scopeMembership.length ? (
      <div className="space-y-0.5" data-testid="scope-membership">
        {line.scopeMembership.map((member, index) => (
          <p key={index} className="text-[10px]">
            {member.materialName || shortId(member.recordId)}
            <span className="text-muted-foreground"> — {line.scopeMembershipLabel}</span>
          </p>
        ))}
        {line.scopeMembershipTruncated ? (
          <Caveat testId="membership-truncated">{COMMERCIAL_COPY.listCutShort}</Caveat>
        ) : null}
        <Caveat>{line.scopeMembershipBasis}</Caveat>
      </div>
    ) : null}
  </div>
);

const SequenceStep = ({ step }) => (
  <div className="rounded border border-border/60 px-2.5 py-2 space-y-0.5" data-testid="ledger-step">
    <div className="flex flex-wrap items-start justify-between gap-2">
      <span className="text-xs font-medium">
        {step.label}
        {step.isCeiling ? (
          <Badge
            variant="outline"
            className="ml-1.5 text-[10px] border-indigo-500/40 text-indigo-300"
            data-testid="ceiling-step"
          >
            the furthest point evidenced
          </Badge>
        ) : null}
      </span>
      <AttributionChip attribution={step.attribution} />
    </div>
    <p className="text-[10px] text-muted-foreground">
      {step.observedAt && step.observedAt !== "UNKNOWN"
        ? formatDay(step.observedAt)
        : "no recorded date"}
      {step.packageId && step.packageId !== "UNKNOWN" ? ` · package ${shortId(step.packageId)}` : ""}
      {` · written by ${step.actorLabel}`}
      {step.actorId ? ` (${step.actorId})` : ""}
    </p>
    <Caveat testId="actor-caveat">{step.actorCaveat}</Caveat>
    <Caveat>{step.basis}</Caveat>
  </div>
);

const CommercialLedgerPanel = ({ payload }) => {
  const [open, setOpen] = useState(false);
  const ledger = presentCommercialLedger(payload);
  if (!ledger) return null;

  const { commercialHistory, procurement, schedule, quality, sequence, executionHistory, identity } =
    ledger;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card className="border-border" data-testid="commercial-ledger">
        <CardHeader className="pb-0">
          <CollapsibleTrigger asChild>
            <button type="button" className="w-full flex items-start justify-between gap-2 text-left">
              <span className="min-w-0">
                <CardTitle className="text-base flex items-center gap-2">
                  <Receipt className="w-4 h-4 text-primary" />
                  {COMMERCIAL_COPY.sectionTitle}
                </CardTitle>
                <span className="text-xs text-muted-foreground leading-relaxed">
                  {COMMERCIAL_COPY.sectionLead}
                </span>
              </span>
              <ChevronDown
                className={`w-4 h-4 text-muted-foreground shrink-0 mt-1 transition-transform ${open ? "rotate-180" : ""}`}
              />
            </button>
          </CollapsibleTrigger>
        </CardHeader>
        <CollapsibleContent>
          <CardContent className="space-y-4 pt-3">
            {/* THE CEILING — a boundary on proof, never a badge */}
            {ledger.ceiling.statement ? (
              <div
                className="rounded border border-border px-3 py-2 space-y-1"
                data-testid="ledger-ceiling"
              >
                <p className="text-[11px] font-medium">{COMMERCIAL_COPY.ceilingTitle}</p>
                <p className="text-[11px] leading-relaxed">{ledger.ceiling.statement}</p>
                <Caveat testId="ceiling-not-a-badge">{ledger.ceiling.notABadge}</Caveat>
                <Caveat>{ledger.ceiling.hingeNote}</Caveat>
                <Caveat testId="identityless-rule">{ledger.ceiling.identitylessQuoteRule}</Caveat>
              </div>
            ) : null}

            {/* IDENTITY LINEAGE — every key named, never reconciled */}
            {identity && (identity.multipleKeys || identity.tiebreak) ? (
              <Section
                title={COMMERCIAL_COPY.identityTitle}
                icon={Fingerprint}
                testId="ledger-identity"
              >
                {identity.historicalBidderKeys.map((key) => (
                  <p key={key.bidderKey} className="text-[11px]" data-testid="bidder-key">
                    <span className="font-mono">{key.bidderKey}</span>
                    {key.isTodaysResolution ? (
                      <span className="text-muted-foreground"> · the key this tenant would use now</span>
                    ) : null}
                  </p>
                ))}
                <Caveat testId="multiple-keys">{identity.multipleKeysNote}</Caveat>
                <Caveat testId="tiebreak">{identity.tiebreakNote}</Caveat>
              </Section>
            ) : null}

            {/* SHORTLIST — an absent row proves nothing */}
            {commercialHistory?.considered ? (
              <Section title={COMMERCIAL_COPY.consideredTitle} icon={ListChecks} testId="ledger-considered">
                <p className="text-[11px]" data-testid="considered-headline">
                  {commercialHistory.considered.headline}
                </p>
                <Caveat testId="considered-detail">{commercialHistory.considered.detail}</Caveat>
                <Caveat>{commercialHistory.considered.attributionCaveat}</Caveat>
              </Section>
            ) : null}

            {/* THE FOUR COUNTS — counts, never a funnel */}
            {commercialHistory?.counts ? (
              <Section title={COMMERCIAL_COPY.countsTitle} testId="ledger-counts">
                <div className="flex flex-wrap gap-1.5">
                  {commercialHistory.counts.map((count) => (
                    <Badge
                      key={count.key}
                      variant="outline"
                      className="text-[10px] border-border text-muted-foreground"
                    >
                      {count.label}: {count.value}
                    </Badge>
                  ))}
                </div>
                <Caveat testId="counts-note">{commercialHistory.countsNote}</Caveat>
              </Section>
            ) : null}

            {/* RELEASES — and the three answers to "did they respond?" */}
            {commercialHistory?.releases.length ? (
              <Section
                title={COMMERCIAL_COPY.releasesTitle}
                lead={commercialHistory.releaseVerbsNote}
                testId="ledger-releases"
              >
                {commercialHistory.releases.map((release) => (
                  <ReleaseRow key={release.recordId} release={release} />
                ))}
              </Section>
            ) : null}

            {/* AUTHORIZED QUOTE BY PACKAGE — three shapes, no winner */}
            {commercialHistory?.authorizedByPackage.length ? (
              <Section title={COMMERCIAL_COPY.authorizedTitle} testId="ledger-authorized">
                {commercialHistory.authorizedByPackage.map((entry) => (
                  <div key={entry.packageId} className="space-y-0.5" data-testid="authorized-package">
                    <p className="text-[11px]">
                      <span className="font-mono">{shortId(entry.packageId)}</span>
                      <span className="text-muted-foreground"> · {entry.headline}</span>
                    </p>
                    {entry.candidates.map((candidate, index) => (
                      <p key={index} className="text-[10px] font-mono">
                        {typeof candidate.value === "string"
                          ? shortId(candidate.value)
                          : JSON.stringify(candidate.value)}
                      </p>
                    ))}
                    {entry.shape === "CONFLICT" ? (
                      <Caveat>Every candidate is listed. Nothing here picks between them.</Caveat>
                    ) : null}
                  </div>
                ))}
              </Section>
            ) : null}

            {/* CHAINS THAT NAME NOBODY */}
            {commercialHistory?.unresolvedAwardChains.length ||
            commercialHistory?.unresolvedQuotesOnReleasedPackages.length ? (
              <Section title={COMMERCIAL_COPY.unresolvedTitle} icon={Split} testId="ledger-unresolved">
                {[
                  ...commercialHistory.unresolvedAwardChains,
                  ...commercialHistory.unresolvedQuotesOnReleasedPackages,
                ].map((chain, index) => (
                  <div key={index} className="space-y-0.5" data-testid="unresolved-chain">
                    <p className="text-[11px]">{chain.headline}</p>
                    <Caveat>{chain.detail}</Caveat>
                  </div>
                ))}
              </Section>
            ) : null}

            {/* AWARD FLAGS — an inferred flag is not an outcome */}
            {commercialHistory?.packageAwardFlags.length ? (
              <Section title={COMMERCIAL_COPY.flagsTitle} testId="ledger-flags">
                {commercialHistory.packageAwardFlags.map((flag) => (
                  <div
                    key={flag.recordId}
                    className="rounded border border-border/60 px-2.5 py-2 space-y-1"
                    data-testid="award-flag"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <span className="text-xs">{flag.headline}</span>
                      <AttributionChip attribution={flag.attribution} />
                    </div>
                    <Caveat>{flag.detail}</Caveat>
                    {flag.scopeAssignment ? (
                      <Caveat testId="flag-scope-assignment">
                        A recorded ownership window also names this firm on this package
                        ({flag.scopeAssignment}). A recorded window never satisfies the live
                        commercial check above it.
                      </Caveat>
                    ) : null}
                  </div>
                ))}
              </Section>
            ) : null}

            {/* PROCUREMENT — scope-level, and only scope-level */}
            {procurement ? (
              <Section
                title={COMMERCIAL_COPY.procurementTitle}
                icon={PackageOpen}
                testId="ledger-procurement"
              >
                {procurement.empty ? (
                  <p className="text-[11px] text-muted-foreground" data-testid="procurement-empty">
                    {procurement.emptyNote}
                  </p>
                ) : (
                  procurement.lines.map((line) => <ProcurementLine key={line.recordId} line={line} />)
                )}
                {procurement.linesTruncated ? (
                  <Caveat testId="lines-truncated">
                    {procurement.lineCount} lines in total. {COMMERCIAL_COPY.listCutShort}
                  </Caveat>
                ) : null}
                {procurement.caveats.map((caveat, index) => (
                  <Caveat key={index}>{caveat}</Caveat>
                ))}
                {procurement.withheldColumns.length ? (
                  <div className="space-y-0.5 pt-1" data-testid="withheld-columns">
                    <p className="text-[11px] font-medium flex items-center gap-1.5">
                      <EyeOff className="w-3.5 h-3.5" />
                      {COMMERCIAL_COPY.withheldTitle}
                    </p>
                    <Caveat>{COMMERCIAL_COPY.withheldLead}</Caveat>
                    {procurement.withheldColumns.map((column, index) => (
                      <p key={index} className="text-[10px] text-muted-foreground">
                        <span className="font-mono">{column.column}</span>
                        {column.columnDefault ? ` (defaults to ${column.columnDefault})` : ""} —{" "}
                        {column.reason}
                      </p>
                    ))}
                  </div>
                ) : null}
              </Section>
            ) : null}

            {/* SCHEDULE — correlation, and nothing beyond it */}
            {schedule ? (
              <Section
                title={COMMERCIAL_COPY.scheduleTitle}
                icon={CalendarClock}
                lead={COMMERCIAL_COPY.scheduleLead}
                testId="ledger-schedule"
              >
                {schedule.empty ? (
                  <p className="text-[11px] text-muted-foreground" data-testid="schedule-empty">
                    {schedule.emptyNote}
                  </p>
                ) : (
                  schedule.observations.map((observation) => (
                    <div
                      key={observation.recordId}
                      className="rounded border border-border/60 px-2.5 py-2 space-y-1"
                      data-testid="schedule-observation"
                    >
                      <p className="text-xs">{observation.relation}</p>
                      <p className="text-[10px] text-muted-foreground">
                        planned {formatDay(observation.expectedDelivery)} · arrived{" "}
                        {formatDay(observation.actualDelivery)}
                      </p>
                      <Caveat>{observation.caveat}</Caveat>
                      <p className="text-[10px] text-muted-foreground">
                        isProviderDelay:{" "}
                        <span className="font-mono">{observation.isProviderDelay}</span>
                      </p>
                    </div>
                  ))
                )}
                {schedule.observationsTruncated ? (
                  <Caveat testId="observations-truncated">
                    {schedule.observationCount} comparisons in total. {COMMERCIAL_COPY.listCutShort}
                  </Caveat>
                ) : null}
                {/* Never "0 delays": the list is empty by construction. */}
                <Caveat testId="delay-findings-basis">{schedule.delayFindingsBasis}</Caveat>
              </Section>
            ) : null}

            {/* QUALITY — a human said so, or nobody did */}
            {quality ? (
              <Section title={COMMERCIAL_COPY.qualityTitle} icon={MessageSquare} testId="ledger-quality">
                <p className="text-[11px]" data-testid="quality-headline">
                  {quality.headline}
                </p>
                <Caveat>{quality.basis}</Caveat>
                {quality.statementsTruncated ? (
                  <Caveat testId="quality-floor">{COMMERCIAL_COPY.statementFloor}</Caveat>
                ) : null}
                {quality.neverRead.length ? (
                  <div className="space-y-0.5 pt-1" data-testid="never-read">
                    <p className="text-[10px] font-medium">{COMMERCIAL_COPY.neverReadTitle}</p>
                    {quality.neverRead.map((source, index) => (
                      <p key={index} className="text-[10px] font-mono text-muted-foreground">
                        {source}
                      </p>
                    ))}
                    <Caveat>{quality.neverReadBasis}</Caveat>
                  </div>
                ) : null}
              </Section>
            ) : null}

            {/* HUMAN STATEMENTS — deferred, not repeated */}
            <Section title={COMMERCIAL_COPY.humanJudgmentTitle} testId="ledger-human-judgment">
              <p className="text-[11px]">
                {ledger.humanJudgmentCount} statement
                {ledger.humanJudgmentCount === 1 ? "" : "s"} recorded.
              </p>
              <Caveat testId="human-judgment-deferred">{ledger.humanJudgmentDeferred}</Caveat>
            </Section>

            {/* EXECUTION HISTORY — must be rendered, must not be faked */}
            {executionHistory ? (
              <Alert className="border-border bg-muted/30" data-testid="execution-history">
                <Ban className="h-4 w-4" />
                <AlertDescription className="space-y-1">
                  <p className="text-[11px] font-medium">{COMMERCIAL_COPY.executionTitle}</p>
                  <p className="text-[11px] leading-relaxed">{executionHistory.headline}</p>
                  <Caveat testId="execution-same-for-all">{executionHistory.sameForAll}</Caveat>
                  {executionHistory.missingWriters.map((writer, index) => (
                    <p
                      key={index}
                      className="text-[10px] text-muted-foreground leading-relaxed"
                      data-testid="missing-writer"
                    >
                      · {writer.fact}
                      {writer.column ? (
                        <span className="font-mono"> ({writer.column})</span>
                      ) : null}
                      {" — "}
                      {writer.statusLabel}
                      {writer.detail ? `: ${writer.detail}` : ""}
                    </p>
                  ))}
                </AlertDescription>
              </Alert>
            ) : null}

            {/* THE RECORD LIST — dated rows, arrows undrawn, nothing numbered */}
            {sequence ? (
              <Section title={COMMERCIAL_COPY.sequenceTitle} testId="ledger-sequence">
                <Caveat testId="arrows-note">{sequence.arrowsNote}</Caveat>
                <div className="space-y-1.5">
                  {sequence.steps.map((step, index) => (
                    <SequenceStep key={`${step.sourceId}-${index}`} step={step} />
                  ))}
                </div>
                {sequence.stepsTruncated ? (
                  <Caveat testId="sequence-truncated">
                    {sequence.count} records in total; the listing above is cut short.
                  </Caveat>
                ) : null}
                {sequence.undatedSteps.length ? (
                  <div className="space-y-1.5 pt-1" data-testid="undated-steps">
                    <p className="text-[11px] font-medium">{COMMERCIAL_COPY.undatedTitle}</p>
                    <Caveat testId="undated-note">{sequence.undatedNote}</Caveat>
                    {sequence.undatedSteps.map((step, index) => (
                      <SequenceStep key={`undated-${index}`} step={step} />
                    ))}
                    {sequence.undatedStepsTruncated ? (
                      <Caveat testId="undated-truncated">
                        {sequence.undatedCount} in total. {COMMERCIAL_COPY.listCutShort}
                      </Caveat>
                    ) : null}
                  </div>
                ) : null}
              </Section>
            ) : null}

            {/* RECENCY — exposed, never decayed */}
            {ledger.recency ? (
              <p
                className="text-[10px] text-muted-foreground flex items-start gap-1.5"
                data-testid="ledger-recency"
              >
                <Clock className="w-3 h-3 mt-0.5 shrink-0" />
                <span className="leading-relaxed">
                  {COMMERCIAL_COPY.recencyTitle}: read as of{" "}
                  {ledger.recency.asOf ? formatDay(ledger.recency.asOf) : "an unrecorded moment"}.{" "}
                  {ledger.recency.decayNote}
                </span>
              </p>
            ) : null}
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

export default CommercialLedgerPanel;
